import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MetodologiaCalculoModel} from '@shared/models/metodologia-calculo.model';
import {MetodologiasCalculoUserService} from '@shared/services/metodologias-calculo-user.service';
import {take} from 'rxjs/operators';
import {buildMetodologiaCountsMap, getDefaultMetodologia} from '@shared/components/zona-metodologia-selector/zona-metodologia-selector.utils';

interface ZonaMetodologiasGroup {
  zonaId: number;
  zonaNombre: string;
  enabled: boolean;
  expanded: boolean;
  selectedMetodologiaIds: number[];
  metodologias: MetodologiaCalculoModel[];
}

@Component({
  selector: 'app-zona-metodologia-selector',
  templateUrl: './zona-metodologia-selector.component.html'
})
export class ZonaMetodologiaSelectorComponent implements OnChanges, OnInit {
  @Input() countStart: Date;
  @Input() countEnd: Date;
  @Input() value: number[] = [];
  @Input() expandable = true;
  @Input() multiselect = false;
  @Output() valueChange = new EventEmitter<number[]>();

  metodologias: MetodologiaCalculoModel[] = [];
  metodologiaCounts: {[metodologiaId: number]: number} = {};
  zonas: ZonaMetodologiasGroup[] = [];
  private static nextId = 0;
  groupNamePrefix = `zona-metodologia-${ZonaMetodologiaSelectorComponent.nextId++}`;
  private lastRangeKey: string;
  private countsLoaded = false;

  constructor(private metodologiasCalculoService: MetodologiasCalculoUserService) {}

  ngOnInit(): void {
    this.loadCounts();
    this.metodologiasCalculoService
      .getAll()
      .pipe(take(1))
      .subscribe((metodologias) => {
        this.metodologias = metodologias;
        this.buildGroups();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.countStart || changes.countEnd) {
      this.loadCounts();
      return;
    }

    if (changes.value) {
      this.buildGroups();
    }
  }

  onZonaEnabledChange(zona: ZonaMetodologiasGroup): void {
    if (!zona.enabled) {
      zona.selectedMetodologiaIds = [];
    }

    if (zona.enabled && zona.selectedMetodologiaIds.length === 0) {
      zona.selectedMetodologiaIds = this.getDefaultMetodologiaIds(zona.metodologias);
    }

    this.emitValue();
  }

  toggleZonaExpanded(zona: ZonaMetodologiasGroup): void {
    zona.expanded = !zona.expanded;
  }

  getMetodologiaCount(metodologiaId: number): number {
    return this.metodologiaCounts[metodologiaId] || 0;
  }

  onMetodologiaChange(zona: ZonaMetodologiasGroup, metodologiaId: number): void {
    if (this.multiselect) {
      zona.selectedMetodologiaIds = zona.selectedMetodologiaIds.includes(metodologiaId)
        ? zona.selectedMetodologiaIds.filter((id) => id !== metodologiaId)
        : [...zona.selectedMetodologiaIds, metodologiaId];
    } else {
      zona.selectedMetodologiaIds = [metodologiaId];
    }

    zona.enabled = zona.selectedMetodologiaIds.length > 0;

    this.emitValue();
  }

  selectAll(): void {
    this.zonas.forEach((zona) => {
      zona.enabled = true;

      if (this.multiselect) {
        zona.selectedMetodologiaIds = zona.metodologias.map((metodologia) => metodologia.id);
        return;
      }

      if (zona.selectedMetodologiaIds.length === 0) {
        zona.selectedMetodologiaIds = this.getDefaultMetodologiaIds(zona.metodologias);
      }
    });

    this.emitValue();
  }

  clearAll(): void {
    this.zonas.forEach((zona) => {
      zona.enabled = false;
      zona.selectedMetodologiaIds = [];
    });

    this.emitValue();
  }

  trackByZona(_: number, zona: ZonaMetodologiasGroup): number {
    return zona.zonaId;
  }

  trackByMetodologia(_: number, metodologia: MetodologiaCalculoModel): number {
    return metodologia.id;
  }

  private loadCounts(): void {
    const rangeKey = this.countStart && this.countEnd ? `${this.countStart.getTime()}-${this.countEnd.getTime()}` : 'all';
    if (rangeKey === this.lastRangeKey) return;

    this.lastRangeKey = rangeKey;
    this.countsLoaded = false;
    this.metodologiasCalculoService
      .countEmisionesByMetodologiaCalculo(this.countStart, this.countEnd)
      .pipe(take(1))
      .subscribe((counts) => {
        this.metodologiaCounts = buildMetodologiaCountsMap(counts);
        this.countsLoaded = true;
        this.buildGroups();
      });
  }

  private buildGroups(): void {
    const useDefaults = !this.value?.length;
    if (!this.metodologias.length || (useDefaults && !this.countsLoaded)) return;

    const selectedIds = this.value || [];
    const previousZonas = this.zonas.reduce(
      (acc, zona) => {
        acc[zona.zonaId] = zona;
        return acc;
      },
      {} as {[zonaId: number]: ZonaMetodologiasGroup}
    );

    const grouped = this.metodologias.reduce(
      (acc, metodologia) => {
        const zona = metodologia.zona;
        if (!zona) return acc;

        if (!acc[zona.id]) {
          acc[zona.id] = {
            zonaId: zona.id,
            zonaNombre: zona.nombre,
            enabled: useDefaults,
            expanded: false,
            selectedMetodologiaIds: [],
            metodologias: []
          };
        }

        acc[zona.id].metodologias.push(metodologia);
        return acc;
      },
      {} as {[zonaId: number]: ZonaMetodologiasGroup}
    );

    this.zonas = Object.values(grouped)
      .map((zona) => {
        const metodologias = zona.metodologias.sort((a, b) => a.codigo.localeCompare(b.codigo));
        const selectedMetodologiaIds = this.getSelectedMetodologiaIds(metodologias, selectedIds);
        const defaultMetodologiaIds = this.getDefaultMetodologiaIds(metodologias);
        const previousZona = previousZonas[zona.zonaId];

        return {
          ...zona,
          enabled: useDefaults || selectedMetodologiaIds.length > 0,
          expanded: previousZona?.expanded ?? false,
          selectedMetodologiaIds: useDefaults
            ? defaultMetodologiaIds
            : selectedMetodologiaIds.length > 0
              ? selectedMetodologiaIds
              : previousZona?.selectedMetodologiaIds || defaultMetodologiaIds,
          metodologias
        };
      })
      .sort((a, b) => a.zonaNombre.localeCompare(b.zonaNombre));

    if (useDefaults && this.zonas.length > 0) {
      this.emitValue();
    }
  }

  private getSelectedMetodologiaIds(metodologias: MetodologiaCalculoModel[], selectedIds: number[]): number[] {
    const matchingIds = metodologias.filter((metodologia) => selectedIds.includes(metodologia.id)).map((metodologia) => metodologia.id);
    return this.multiselect ? matchingIds : matchingIds.slice(0, 1);
  }

  private getDefaultMetodologiaIds(metodologias: MetodologiaCalculoModel[]): number[] {
    if (this.multiselect) {
      return metodologias.map((metodologia) => metodologia.id);
    }

    const defaultMetodologia = getDefaultMetodologia(metodologias, this.metodologiaCounts);
    return defaultMetodologia ? [defaultMetodologia.id] : [];
  }

  private emitValue(): void {
    const selectedIds = this.zonas.reduce((acc, zona) => acc.concat(zona.selectedMetodologiaIds), [] as number[]);
    if (selectedIds.length > 0) {
      this.valueChange.emit(selectedIds);
    }
  }
}
